import courseService from '@/shared/services/courseService';
import { selectedProgramsFromSaved } from './draftApplication';
import { meetingTypesOnly, meetingsFromSaved } from './meetings';
import { getDefaultCourseContext } from './CreateApplicationSection4';
import { programResponsesFromSaved } from './CreateApplicationSection5';

// Turns a saved application into the create wizard's starting state for
// "Duplicate this application" (#457). Everything carries over except what
// belongs to one section (CRN, section #, enrollment, meeting days/time/room,
// named students) and the acknowledgments, which are agreements and have to
// be checked again. The kinds of meetings (lecture, lab) stay.
//
// "On behalf of" only carries over when the person duplicating filed the
// original. The instructor it named also sees it under My Applications, and
// their copy shouldn't say it's on behalf of themselves.
export function getDuplicateSeed(app, currentUserId) {
    const saved = app.courseConfirmation || {};
    const programResponses = programResponsesFromSaved(
        app.programSpecificResponses
    );
    const applicantId = app.applicant?._id || app.applicant;
    const onBehalf =
        app.isSubmittedOnBehalf === true &&
        !!currentUserId &&
        String(applicantId) === String(currentUserId);

    return {
        isSubmittedOnBehalf: onBehalf,
        courseConfirmation: {
            crn: '',
            enrollment: '',
            sectionNumber: '',
            // Another section may meet in another part of term, so the
            // copy starts at the term's default
            partOfTermCode: '',
            partOfTermName: '',
            instructorOfRecordName: onBehalf
                ? saved.instructorOfRecordName || ''
                : '',
            instructorOfRecordEmail: onBehalf
                ? saved.instructorOfRecordEmail || ''
                : '',
            modality: saved.modality || '',
            otherModality: saved.otherModality || '',
            meetings: meetingTypesOnly(meetingsFromSaved(saved)),
        },
        courseContext: {
            ...getDefaultCourseContext(),
            ...(app.courseContext || {}),
        },
        programResponses: {
            ...programResponses,
            // Chair approval is for one request, so it's asked again too
            ta: {
                ...programResponses.ta,
                chairApprovalObtained: false,
                acknowledgesLAExclusion: false,
            },
            la: {
                ...programResponses.la,
                // A nominee is for one section, so ask again
                nomineeStatus: null,
                nomineeDeclinePreference: '',
                acknowledgesContract: false,
                acknowledgesLARequirements: false,
            },
        },
        selectedPrograms: selectedProgramsFromSaved(app.programPreferences),
    };
}

// Drops copied programs the course isn't eligible for, or whose window is
// closed this term, and re-ranks the rest. Either would only be refused at
// submit.
export function keepEligiblePrograms(
    selectedPrograms,
    eligiblePrograms,
    applicationWindows
) {
    const kept = selectedPrograms.filter(
        (p) =>
            (!eligiblePrograms || eligiblePrograms.includes(p.key)) &&
            // Same test Section 2 uses for "accepting"
            (!applicationWindows?.[p.key] ||
                applicationWindows[p.key].accepting)
    );
    if (kept.length === selectedPrograms.length) return selectedPrograms;
    return kept.map((p, i) => ({ ...p, rank: i + 1 }));
}

// Courses are one row per term, so an application from an earlier term
// points at last term's copy. Finds this term's copy by subject and number,
// or returns null if the catalog doesn't have it.
export async function findCourseForTerm(sourceCourse, termCode) {
    if (!sourceCourse) return null;
    // No open term to look in. The wizard stops before this in that case.
    if (!termCode) return null;
    if (sourceCourse.term === termCode) return sourceCourse._id;
    try {
        const { courses = [] } = await courseService.searchCourses({
            term: termCode,
            subject: sourceCourse.subject,
            courseNumber: sourceCourse.courseNumber,
        });
        const matches = courses.filter(
            (c) =>
                c.subject === sourceCourse.subject &&
                c.courseNumber === sourceCourse.courseNumber
        );
        // Prefer the catalog's base row over a section's row or a typed-in
        // course, same order the server's unlisted-course lookup uses
        const isCatalog = (c) => c.sourceSystem !== 'applicant';
        const match =
            matches.find((c) => isCatalog(c) && !c.section) ||
            matches.find(isCatalog) ||
            matches[0];
        return match?._id || null;
    } catch {
        return null;
    }
}
